import { useCallback, useEffect, useRef, useState } from 'react'
import { useMainButton } from '../components/MainButton'
import { RichText } from '../components/RichText'
import { ThemeChips } from '../components/ThemeChips'
import { loc } from '../data/cards'
import {
  formatById,
  formatName,
  PALM_MARKERS,
  PERSONA_CAPTION,
  PERSONA_FAREWELL,
  PERSONA_FORMAT,
  RESULT_TITLE,
  SHOOT_INTRO,
  SHOOT_TIPS,
  type FormatDef,
  type FormatId,
} from '../data/formats'
import { formatDate } from '../i18n'
import { api, ApiError, type FortuneRecord, type FortuneResponse } from '../lib/api'
import { track } from '../lib/analytics'
import { preparePhoto } from '../lib/photo'
import { revealField } from '../lib/viewport'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'

/**
 * Шаги гадания без карт.
 *
 * Один экран на все пять форматов: у них общий хребет (что-то дать → подождать
 * → прочитать), а различия — только в том, что просят на входе и что рисуют
 * якорем в результате. Разводить это по пяти экранам значило бы пять раз
 * написать ожидание, ошибку и разбор.
 */
type Step = 'intro' | 'preview' | 'waiting' | 'result' | 'error' | 'journal' | 'input'

interface Props {
  format: FormatId
  /** Не хватает звёзд — уводим на Premium. */
  onPaywall: (cost: number) => void
}

/** С какого шага начинается формат. У снов вход — дневник, а не пустое поле. */
function entryStep(def: FormatDef): Step {
  return def.kind === 'text' ? 'journal' : 'intro'
}

export function Fortune({ format, onPaywall }: Props) {
  const { profile, lang, t, update } = useSirr()
  const def = formatById(format)!

  const [step, setStep] = useState<Step>(() => entryStep(def))
  const [photo, setPhoto] = useState<string | null>(null)
  /** Текст сна или желание джинну — то, что человек пишет сам. */
  const [text, setText] = useState('')
  const [result, setResult] = useState<FortuneResponse | null>(null)
  const [journal, setJournal] = useState<FortuneRecord[] | null>(null)
  /** Персонаж отвечает — поле ввода заперто, в ленте многоточие. */
  const [sending, setSending] = useState(false)
  const [replyFailed, setReplyFailed] = useState(false)
  /** Оплаченный разговор, в котором остались ходы: продолжаем, а не платим снова. */
  const [resumable, setResumable] = useState<FortuneRecord | null>(null)

  const fileRef = useRef<HTMLInputElement>(null)
  /** Открыть камеру или галерею — разные `capture` у одного и того же input. */
  const pickRef = useRef<'camera' | 'gallery'>('camera')

  // Колбэк из App пересоздаётся на каждый его рендер; в зависимостях эффекта
  // он перезапускал бы запрос, а запрос здесь платный.
  const paywallRef = useRef(onPaywall)
  paywallRef.current = onPaywall

  const promptRef = useRef({ goals: profile.goals, theme: profile.theme })
  promptRef.current = { goals: profile.goals, theme: profile.theme }

  useEffect(() => {
    track('fortune_open', { format, cost: def.cost })
  }, [format, def.cost])

  // Дневник подтягиваем один раз при входе в сны: он же служит и историей.
  useEffect(() => {
    if (def.kind !== 'text' || journal !== null) return
    let cancelled = false
    api
      .fortunes('dream')
      .then((res) => {
        if (!cancelled) setJournal(res.fortunes)
      })
      .catch(() => {
        // Сервер недоступен — показываем пустой дневник, а не поломку.
        if (!cancelled) setJournal([])
      })
    return () => {
      cancelled = true
    }
  }, [def.kind, journal])

  /**
   * Недоговорённый разговор.
   *
   * Человек заплатил за три желания, задал одно и вышел — вернувшись, он
   * должен продолжить, а не платить второй раз за то же самое. Защита от
   * повторной оплаты (`recentIdenticalFortune`) живёт десять минут и на это
   * не рассчитана: она про двойное нажатие, а не про «зашёл вечером».
   */
  useEffect(() => {
    if (def.kind !== 'persona') return
    let cancelled = false
    api
      .fortunes(format)
      .then((res) => {
        const open = res.fortunes.find((f) => f.turnsLeft > 0)
        if (!cancelled && open) setResumable(open)
      })
      .catch(() => {
        // Не достучались — просто предложим начать заново; хуже не будет.
      })
    return () => {
      cancelled = true
    }
  }, [def.kind, format])

  const askOracle = useCallback(async () => {
    setStep('waiting')
    haptic.impact('medium')
    try {
      const res = await api.fortune({
        format,
        lang,
        image: photo ?? undefined,
        text: text.trim() || undefined,
        theme: promptRef.current.theme ?? undefined,
        goals: promptRef.current.goals,
      })
      if (typeof res.stars === 'number') update({ stars: res.stars })
      setResult(res)
      setStep('result')
      haptic.notify('success')
      // Свежая запись оказывается в дневнике первой — не ходим на сервер снова.
      if (def.kind === 'text') {
        setJournal((list) => [{ ...res, lang, input: text.trim() }, ...(list ?? [])])
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'insufficient_stars') {
        haptic.notify('error')
        paywallRef.current(def.cost)
        return
      }
      // Звёзды сервер уже вернул — на экране ошибки так и написано.
      haptic.notify('error')
      setStep('error')
    }
  }, [format, lang, photo, text, def.cost, def.kind, update])

  /**
   * Очередная реплика человека. Звёзды не списываются — за разговор
   * заплачено на входе, и границу держит счётчик ходов на сервере.
   */
  const sendReply = useCallback(
    async (message: string) => {
      if (!result) return
      setSending(true)
      // Свою реплику показываем сразу: ждать ответа персонажа, глядя на пустое
      // поле, — это выглядит так, будто сообщение не отправилось.
      setResult((r) => (r ? { ...r, chat: [...r.chat, { who: 'u', text: message }] } : r))
      haptic.impact('light')
      try {
        const res = await api.reply(result.id, {
          lang,
          message,
          theme: promptRef.current.theme ?? undefined,
          goals: promptRef.current.goals,
        })
        setResult((r) => (r ? { ...r, chat: res.chat, turnsLeft: res.turnsLeft } : r))
        haptic.notify('success')
      } catch {
        // Ход сервер вернул. Снимаем свою реплику, чтобы её можно было
        // отправить заново, а не гадать, дошла она или нет.
        setResult((r) => (r ? { ...r, chat: r.chat.slice(0, -1) } : r))
        haptic.notify('error')
        setReplyFailed(true)
      } finally {
        setSending(false)
      }
    },
    [result],
  )

  const choosePhoto = (source: 'camera' | 'gallery') => {
    pickRef.current = source
    const input = fileRef.current
    if (!input) return
    // `capture` включает камеру, его отсутствие — обычный выбор файла.
    if (source === 'camera') input.setAttribute('capture', 'environment')
    else input.removeAttribute('capture')
    input.click()
  }

  const onFile = async (file: File | undefined) => {
    if (!file) return
    try {
      setPhoto(await preparePhoto(file))
      setStep('preview')
      haptic.impact('light')
    } catch {
      haptic.notify('error')
      setStep('error')
    }
  }

  /**
   * Разговор идёт — главной кнопки нет, её место занимает поле ввода.
   *
   * Поле живёт в самой ленте, а не в нижней полосе. Прижатое к низу, оно
   * заставляло iOS показывать его при фокусе, а показать он мог только сдвинув
   * весь каркас — приложение уезжало под строку статуса, и вернуть его удавалось
   * только свайпом (три попытки чинить это в `viewport.ts` ничего не дали).
   * В ленте фокус попадает внутрь `.app__body` — единственного настоящего
   * контейнера прокрутки, — и браузер прокручивает именно его, как и задумано.
   */
  const chatting = step === 'result' && def.kind === 'persona' && (result?.turnsLeft ?? 0) > 0

  // ── Главная кнопка ────────────────────────────────────────────────
  useMainButton(
    chatting
      ? null
      :
    step === 'intro' && def.kind === 'photo'
      ? null // на ритуале главное действие — «Снять», оно уже на экране
      : step === 'intro' && def.kind === 'persona'
        ? resumable
          ? {
              // Разговор уже оплачен — возвращаемся в него бесплатно.
              label: t('talkResume'),
              onClick: () => {
                setResult(resumable)
                setStep('result')
                haptic.impact('light')
              },
            }
          : {
              label: def.id === 'djinn' ? t('wishMake') : t('neneHear'),
              onClick: () => void askOracle(),
            }
        : step === 'preview'
          ? { label: t('readPhoto'), onClick: () => void askOracle() }
          : step === 'waiting'
            ? { label: t('reading'), loading: true, onClick: () => {} }
            : step === 'error'
              ? {
                  label: def.kind === 'photo' ? t('retake') : t('retry'),
                  onClick: () => setStep(def.kind === 'photo' ? 'intro' : entryStep(def)),
                }
              : step === 'journal'
                ? { label: t('dreamRecord'), onClick: () => { setText(''); setStep('input') } }
                : step === 'input'
                  ? {
                      label: t('dreamInterpret'),
                      disabled: text.trim().length < 10,
                      onClick: () => void askOracle(),
                    }
                  : def.kind === 'text'
                    ? { label: t('dreamJournal'), onClick: () => setStep('journal') }
                    : {
                        label: t('fortuneAgain'),
                        onClick: () => {
                          setPhoto(null)
                          setResult(null)
                          setStep(entryStep(def))
                        },
                      },
  )

  return (
    <div className="screen">
      {/* Один input на оба источника: камеру включает атрибут capture. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void onFile(e.target.files?.[0])
          // Сбрасываем, иначе повторный выбор того же файла не вызовет change.
          e.target.value = ''
        }}
      />

      {step === 'intro' && def.kind === 'photo' && <PhotoIntro def={def} onPick={choosePhoto} />}
      {step === 'intro' && def.kind === 'persona' && (
        <PersonaIntro def={def} resumable={Boolean(resumable)} />
      )}
      {step === 'preview' && photo && (
        <PhotoPreview def={def} photo={photo} onRetake={() => choosePhoto('camera')} />
      )}
      {step === 'waiting' && <Waiting def={def} />}
      {step === 'error' && <Failure def={def} />}
      {step === 'journal' && <Journal entries={journal} onOpen={(r) => { setResult(r); setStep('result') }} />}
      {step === 'input' && <DreamInput value={text} onChange={setText} />}
      {step === 'result' && result && def.kind === 'persona' && (
        <PersonaChat
          def={def}
          result={result}
          sending={sending}
          failed={replyFailed}
          resumed={result.id === resumable?.id}
          onSend={(m) => {
            setReplyFailed(false)
            void sendReply(m)
          }}
        />
      )}
      {step === 'result' && result && def.kind !== 'persona' && (
        <Result def={def} result={result} photo={photo} />
      )}
    </div>
  )
}

// ── Шаги ────────────────────────────────────────────────────────────

function PhotoIntro({ def, onPick }: { def: FormatDef; onPick: (s: 'camera' | 'gallery') => void }) {
  const { lang, t } = useSirr()
  const tips = SHOOT_TIPS[def.id as 'coffee' | 'palm']

  return (
    <>
      <div style={{ textAlign: 'center', marginBottom: 18 }}>
        <div className="fortune__sigil">{def.sym}</div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 23 }}>
          {formatName(def, lang)}
        </div>
        <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', marginTop: 6 }}>
          {loc(SHOOT_INTRO[def.id as 'coffee' | 'palm'], lang)}
        </p>
      </div>

      <ThemeChips compact />

      <div className="eyebrow" style={{ margin: '16px 0 12px' }}>
        {t('howToShoot')}
      </div>
      <ol className="tips">
        {tips.map((tip, i) => (
          <li key={i}>
            <span className="tips__n">{i + 1}</span>
            <span className="tips__text">{loc(tip, lang)}</span>
          </li>
        ))}
      </ol>

      <div style={{ display: 'flex', gap: 11, marginTop: 22 }}>
        <button type="button" className="fortune__shoot" onClick={() => onPick('camera')}>
          ◉ {t('shoot')}
        </button>
        <button type="button" className="fortune__gallery" onClick={() => onPick('gallery')}>
          ▦ {t('fromGallery')}
        </button>
      </div>

      <p style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: 'rgba(200,162,78,.75)' }}>
        {t('fortuneCost', { n: def.cost })}
      </p>
    </>
  )
}

function PhotoPreview({ def, photo, onRetake }: { def: FormatDef; photo: string; onRetake: () => void }) {
  const { t } = useSirr()
  return (
    <div style={{ textAlign: 'center', paddingTop: 6 }}>
      <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', marginBottom: 18 }}>
        {t('previewHint')}
      </p>
      <img
        src={photo}
        alt=""
        className={def.id === 'palm' ? 'fortune__shot fortune__shot--palm' : 'fortune__shot'}
      />
      <button type="button" className="fortune__retake" onClick={onRetake}>
        ↺ {t('retake')}
      </button>
    </div>
  )
}

function Waiting({ def }: { def: FormatDef }) {
  const { t } = useSirr()
  const copy = {
    coffee: 'waitCoffee', palm: 'waitPalm', dream: 'waitDream',
    djinn: 'waitDjinn', nene: 'waitNene',
  } as const

  return (
    <div className="fortune__centered">
      <div className="fortune__orbit">
        <div className="fortune__orbit-ring" />
        <div className="fortune__orbit-core">{def.sym}</div>
      </div>
      <div className="shimmer" style={{ width: 180 }} />
      <div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 21 }}>
          {t(copy[def.id])}
        </div>
        <div style={{ fontFamily: 'var(--serif-body)', fontSize: 15, color: 'var(--ink-faint)', marginTop: 4 }}>
          {t('waitSub')}
        </div>
      </div>
    </div>
  )
}

/**
 * Отказ.
 *
 * У гаданий по фото самая частая причина — снимок, на котором ничего не
 * разобрать, поэтому вместо общего «попробуйте ещё раз» повторяем правила
 * съёмки: они и есть ответ на вопрос «что я сделал не так».
 */
function Failure({ def }: { def: FormatDef }) {
  const { lang, t } = useSirr()
  const photo = def.kind === 'photo'
  const tips = photo ? SHOOT_TIPS[def.id as 'coffee' | 'palm'] : []

  return (
    <div className="fortune__centered">
      <div className="fortune__warn">⚠</div>
      <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 23 }}>
        {photo ? t('photoErrTitle') : formatName(def, lang)}
      </div>
      <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', maxWidth: 260 }}>
        {photo ? t('photoErrBody') : t('fortuneFailed')}
      </p>
      {photo && (
        <>
          <ul className="tips tips--plain">
            {tips.map((tip, i) => (
              <li key={i}>
                <span className="tips__dot">·</span>
                <span className="tips__text">{loc(tip, lang)}</span>
              </li>
            ))}
          </ul>
          {/* Про возврат звёзд говорим прямо: списание человек уже видел,
              и молчание здесь читается как «деньги пропали». */}
          <p style={{ fontSize: 12, color: 'var(--ink-faint)', margin: 0 }}>{t('fortuneFailed')}</p>
        </>
      )}
    </div>
  )
}

/**
 * Портрет персонажа в золотой рамке.
 *
 * Символ формата остаётся запасным вариантом: картинка — отдельный файл, и
 * если он не доехал (медленная сеть, старый кеш установленной PWA), рамка
 * не должна оказаться пустой. Символ здесь именно то, что было на её месте
 * до появления иллюстраций.
 */
function PersonaPortrait({ def, small = false }: { def: FormatDef; small?: boolean }) {
  const [failed, setFailed] = useState(false)
  const { lang } = useSirr()

  return (
    <div className={`persona persona--${def.id}${small ? ' persona--small' : ''}`}>
      <div className="persona__frame">
        {failed ? (
          <span className="persona__sym">{def.sym}</span>
        ) : (
          <img
            src={`/personas/${def.id}.jpg`}
            // Имя персонажа стоит прямо под портретом, повторять его в alt —
            // значит заставить читалку прочитать его дважды.
            alt=""
            aria-hidden
            title={formatName(def, lang)}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  )
}

function PersonaIntro({ def, resumable }: { def: FormatDef; resumable: boolean }) {
  const { lang, t } = useSirr()
  const who = def.id as 'djinn' | 'nene'

  return (
    <div className="fortune__centered" style={{ gap: 16 }}>
      <PersonaPortrait def={def} />
      <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 27 }}>
        {formatName(def, lang)}
      </div>
      <div className="persona__badge">{loc(PERSONA_FORMAT[who], lang)}</div>
      <p className="prose" style={{ fontSize: 17, color: 'var(--ink-soft)', maxWidth: 280 }}>
        {loc(PERSONA_CAPTION[who], lang)}
      </p>

      {/* За начатый разговор уже заплачено — цену показывать нельзя, иначе
          человек решит, что с него спишут второй раз. */}
      <p style={{ fontSize: 11, color: 'rgba(200,162,78,.75)', margin: 0 }}>
        {resumable ? t('talkUnfinished') : t('fortuneCost', { n: def.cost })}
      </p>
    </div>
  )
}

function DreamInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useSirr()
  return (
    <>
      <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', marginBottom: 14 }}>
        {t('dreamIntro')}
      </p>
      <textarea
        className="fortune__dream"
        value={value}
        maxLength={2000}
        placeholder={t('dreamPlaceholder')}
        onChange={(e) => onChange(e.target.value)}
      />
      <div style={{ marginTop: 14 }}>
        <ThemeChips compact />
      </div>
    </>
  )
}

function Journal({ entries, onOpen }: { entries: FortuneRecord[] | null; onOpen: (r: FortuneRecord) => void }) {
  const { lang, t } = useSirr()

  if (entries === null) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="shimmer" style={{ height: 62, borderRadius: 14 }} />
        ))}
      </div>
    )
  }

  if (!entries.length) {
    return (
      <div className="fortune__centered" style={{ minHeight: 440 }}>
        <div className="fortune__empty">❋</div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22 }}>
          {t('dreamEmptyTitle')}
        </div>
        <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', maxWidth: 250 }}>
          {t('dreamEmptyBody')}
        </p>
      </div>
    )
  }

  return (
    <>
      <p className="prose" style={{ fontSize: 16, color: 'var(--ink-muted)', marginBottom: 16 }}>
        {t('dreamJournalIntro')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {entries.map((entry) => (
          <button key={entry.id} type="button" className="journal__entry" onClick={() => onOpen(entry)}>
            <span className="journal__sym">❋</span>
            <span className="journal__body">
              <span className="journal__head">
                <span className="journal__title">{entry.title || t('dreamJournal')}</span>
                <span className="journal__date">{formatDate(entry.ts, lang)}</span>
              </span>
              <span className="journal__snippet">{entry.input || entry.overall}</span>
            </span>
          </button>
        ))}
      </div>
    </>
  )
}

// ── Разговор с персонажем ───────────────────────────────────────────

type FortuneChatLine = FortuneResponse['chat'][number]

/** Длина куска, после которой реплику лучше разбить на два пузыря. */
const BUBBLE_CHARS = 140

/**
 * Режет реплику персонажа на куски по границам предложений.
 *
 * Целая реплика в одном пузыре читается как заготовка: человек видит стену
 * текста, которая появилась мгновенно, и понимает, что её никто не печатал.
 * Разбитая на два-три куска с паузами, та же реплика звучит как речь.
 */
function splitReply(text: string): string[] {
  const sentences = text.match(/[^.!?…]+[.!?…]*\s*/g) ?? [text]
  const parts: string[] = []
  let current = ''
  for (const sentence of sentences) {
    // Копим, пока кусок не дорос: одно предложение на пузырь — это уже рубка,
    // а не речь.
    if (current && (current + sentence).length > BUBBLE_CHARS) {
      parts.push(current.trim())
      current = sentence
    } else {
      current += sentence
    }
  }
  if (current.trim()) parts.push(current.trim())
  return parts.length ? parts : [text]
}

/**
 * Сколько «печатается» кусок.
 *
 * Прежний потолок в 1,6 секунды срезал почти любую реплику, и длина переставала
 * что-либо значить: всё появлялось одинаково быстро и выдавало заготовку.
 * Теперь считаем честно, а потолок стоит там, куда дотягивается только совсем
 * длинный кусок. 24 мс на символ — примерно 40 знаков в секунду: быстрая, но
 * человеческая скорость. Полсекунды сверху — пауза на «собраться с мыслями».
 */
function typingDelay(text: string): number {
  return Math.min(4200, 500 + text.length * 24)
}

/**
 * Выдаёт разговор постепенно.
 *
 * Свои реплики человека показываем сразу — он их только что написал. Реплики
 * персонажа приходят с сервера целиком, но выкладываются по кускам, с точками
 * между ними: разговор должен идти, а не появляться.
 *
 * Уже прочитанный разговор (вернулись к недоговорённому) разворачивается
 * целиком: заново «печатать» прочитанное — издевательство.
 */
function useStagedChat(chat: FortuneChatLine[], instantCount: number) {
  const [shown, setShown] = useState<FortuneChatLine[]>(() => chat.slice(0, instantCount))
  const [typing, setTyping] = useState(false)
  /** Сколько реплик разговора уже разобрано: показанных кусков больше. */
  const taken = useRef(instantCount)

  useEffect(() => {
    // Реплика снята после сбоя — разговор стал короче, чем мы показали.
    if (chat.length < taken.current) {
      taken.current = chat.length
      setShown(chat)
      setTyping(false)
      return
    }
    if (chat.length === taken.current) return

    const next = chat.slice(taken.current)
    taken.current = chat.length

    let cancelled = false
    const timers: ReturnType<typeof setTimeout>[] = []
    let at = 0

    for (const line of next) {
      // Свою реплику человек только что написал — она появляется сразу.
      if (line.who === 'u') {
        setShown((s) => [...s, line])
        continue
      }
      for (const part of splitReply(line.text)) {
        at += typingDelay(part)
        timers.push(
          setTimeout(() => {
            if (!cancelled) setShown((s) => [...s, { who: 'p', text: part }])
          }, at),
        )
      }
    }

    if (at > 0) {
      setTyping(true)
      timers.push(setTimeout(() => !cancelled && setTyping(false), at))
    }

    return () => {
      cancelled = true
      for (const timer of timers) clearTimeout(timer)
    }
  }, [chat])

  return { shown, typing }
}

/**
 * Лента разговора.
 *
 * Портрет уезжает в шапку ленты и остаётся маленьким: в разговоре важно, что
 * персонаж говорит, а не как он выглядит, — большой портрет вытеснял бы
 * первые реплики за экран.
 */
function PersonaChat({
  def, result, sending, failed, resumed, onSend,
}: {
  def: FormatDef
  result: FortuneResponse
  sending: boolean
  failed: boolean
  /** Вернулись в недоговорённый разговор — прочитанное показываем целиком. */
  resumed: boolean
  onSend: (message: string) => void
}) {
  const { lang, t } = useSirr()
  const who = def.id as 'djinn' | 'nene'
  const over = result.turnsLeft <= 0
  // Прочитанное разворачивается сразу, всё новое — по кускам, с паузами.
  const instantCount = useRef(resumed ? result.chat.length : 0).current
  const { shown, typing } = useStagedChat(result.chat, instantCount)

  /**
   * Новая реплика должна оказаться на виду сама.
   *
   * Двигаем ленту напрямую, а не через `scrollIntoView`: тот прокручивает всех
   * прокручиваемых предков разом — включая каркас `.app`. Каркас объявлен
   * `overflow: hidden`, но программно поддаётся, и тогда наверх уезжает всё
   * приложение вместе с шапкой.
   */
  useEffect(() => {
    const body = document.querySelector('.app__body')
    if (body) body.scrollTop = body.scrollHeight
  }, [shown.length, sending, typing])

  return (
    <div className="talk">
      <div className="talk__head">
        <PersonaPortrait def={def} small />
        <div>
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 20 }}>
            {formatName(def, lang)}
          </div>
          <div className="talk__turns">
            {over ? loc(PERSONA_FORMAT[who], lang) : t('turnsLeft', { n: result.turnsLeft })}
          </div>
        </div>
      </div>

      <div className="talk__list">
        {shown.map((line, i) => (
          <div key={i} className={`bubble bubble--${line.who}`}>
            <RichText text={line.text} />
          </div>
        ))}

        {(sending || typing) && (
          <div className="bubble bubble--p bubble--typing">
            <span /><span /><span />
          </div>
        )}

        {failed && <div className="talk__failed">{t('replyFailed')}</div>}

        {/* Прощание пишем мы, а не модель: последнее слово должно быть одним
            и тем же, иначе конец разговора не отличить от поломки. Ждём, пока
            персонаж договорит, — иначе оно обгоняет его последнюю реплику. */}
        {over && !typing && (
          <div className="talk__farewell">{loc(PERSONA_FAREWELL[who], lang)}</div>
        )}
      </div>

      {/* Поле ввода — последним в ленте, а не прижатым к низу экрана: так
          клавиатуру обрабатывает контейнер прокрутки, а не каркас. */}
      {!over && (
        <Composer
          placeholder={def.id === 'djinn' ? t('wishPlaceholder') : t('askPlaceholder')}
          disabled={sending}
          onSend={onSend}
        />
      )}
    </div>
  )
}

/** Поле ввода реплики. Живёт в нижней полосе, на месте главной кнопки. */
function Composer({
  placeholder, disabled, onSend,
}: {
  placeholder: string
  disabled: boolean
  onSend: (message: string) => void
}) {
  const [draft, setDraft] = useState('')
  const ready = draft.trim().length > 0 && !disabled

  /**
   * Показываем поле, если его накрыла клавиатура.
   *
   * Общий помощник, а не своя прокрутка: ровно то же понадобилось вопросу по
   * готовому раскладу, и две реализации одного разошлись бы при первой правке.
   * Что он двигает и почему тут нельзя `scrollIntoView` — в шапке `revealField`.
   */
  const send = () => {
    if (!ready) return
    onSend(draft.trim())
    setDraft('')
  }

  return (
    <div className="composer">
      <input
        type="text"
        value={draft}
        maxLength={500}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={(e) => revealField(e.currentTarget)}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') send()
        }}
      />
      <button type="button" disabled={!ready} aria-label="→" onClick={send}>
        ↑
      </button>
    </div>
  )
}

// ── Результат ───────────────────────────────────────────────────────

/**
 * Общий шаблон результата: меняется только якорь наверху, тело толкования
 * у всех форматов одно. Ради этого он и общий — у сна, кофе и персонажей
 * карт нет, а доска карт была единственным, что держало прежний экран.
 */
function Result({ def, result, photo }: { def: FormatDef; result: FortuneResponse; photo: string | null }) {
  const { lang, t } = useSirr()
  const persona = def.kind === 'persona'

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
        {def.kind === 'photo' && photo ? (
          <div className={def.id === 'palm' ? 'anchor anchor--palm' : 'anchor anchor--cup'}>
            <img src={photo} alt="" />
            {/* Номера линий совпадают с порядком разбора: подписи ставит
                сервер из общего списка, а не модель. */}
            {def.id === 'palm' &&
              PALM_MARKERS.map((m, i) => (
                <span key={i} className="anchor__marker" style={{ left: m.x, top: m.y }}>
                  {i + 1}
                </span>
              ))}
          </div>
        ) : persona ? (
          <PersonaPortrait def={def} small />
        ) : (
          <div className="fortune__sigil">{def.sym}</div>
        )}

        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22, marginTop: 12, textAlign: 'center' }}>
          {result.title || loc(RESULT_TITLE[def.id], lang)}
        </div>
      </div>

      <div className="fortune__panel" style={{ animation: 'fu .8s both' }}>
        <div className="eyebrow" style={{ marginBottom: persona ? 14 : 10 }}>
          {persona ? t('dialogue') : t('interpretation')}
        </div>

        {persona ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {result.chat.map((line, i) => (
              <div key={i} className={`bubble bubble--${line.who}`}>
                <RichText text={line.text} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="fortune__overall">
              <RichText text={result.overall} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
              {result.sections.map((section, i) => (
                <div key={i} style={{ display: 'flex', gap: 11 }}>
                  <div className="fortune__mark">{def.id === 'palm' ? i + 1 : def.sym}</div>
                  <div>
                    <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15 }}>
                      {section.label}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--serif-body)',
                        fontSize: 15,
                        lineHeight: 1.45,
                        color: '#c9bcd8',
                        marginTop: 2,
                      }}
                    >
                      <RichText text={section.text} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {def.kind === 'text' && (
        <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-faint)', marginTop: 12 }}>
          {t('dreamSaved')}
        </p>
      )}
    </>
  )
}
